import type { Dispatch, SetStateAction } from "react"
import { useSnackbar } from "notistack"

import type { OutlookBookingSuccessArgs } from "../../../modules/order/pages/outlookCreateOrderTypes"

import { CreateOrderDialogController } from "./CreateOrderDialogController"
import { CreateOrderDialogMode, CreateOrderDialogType } from "./createOrderTypes"
import type { CreateOrderDialogFlow, OnBehalfOfUser } from "./createOrderTypes"
import type { CreateOrderType } from "./helpers"
import type { OnBehalfCompletedDialog } from "./hooks/useCreateOrderUser"
import { OrderOnBehalfFlow } from "./orderOnBehalf/OrderOnBehalfFlow"

type CreateOrderDialogProps = Readonly<{
    open: boolean
    onClose: (val: boolean) => void
    activeOrderTab?: CreateOrderType
    dialogType?: CreateOrderDialogType
    orderId?: number
    mode: CreateOrderDialogMode
    flowConfig?: CreateOrderDialogFlow
    onBehalfOf?: OnBehalfOfUser
    handleOutlookBookingCompleted?: (args: OutlookBookingSuccessArgs) => void
    isOnBehalfCompleted: OnBehalfCompletedDialog
    setIsOnBehalfCompleted: Dispatch<SetStateAction<OnBehalfCompletedDialog>>
}>

export function CreateOrderDialog({
    open,
    onClose,
    activeOrderTab,
    dialogType = CreateOrderDialogType.ORDER,
    orderId,
    mode = CreateOrderDialogMode.CREATE,
    flowConfig,
    onBehalfOf,
    handleOutlookBookingCompleted,
    isOnBehalfCompleted,
    setIsOnBehalfCompleted,
}: CreateOrderDialogProps) {
    const { closeSnackbar } = useSnackbar()

    const handleOnComplete = (id: number, name: string, companyId: number) => {
        setIsOnBehalfCompleted((prev) => ({ ...prev, show: false, userId: id, displayName: name, companyId }))
    }

    const handleClose = () => {
        closeSnackbar()
        onClose(false)

        // Note: refreshing the data an order affects is driven by CreateOrderDialogHost, which
        // watches the open -> closed transition and therefore also covers the close paths that
        // never reach this handler (browser/mouse back, route change). The extra-services cache
        // is likewise cleared by the host, on open.
    }

    return isOnBehalfCompleted.show ? (
        <OrderOnBehalfFlow
            userType={isOnBehalfCompleted.userType}
            onComplete={handleOnComplete}
            handleCloseParentDialog={handleClose}
        />
    ) : (
        <CreateOrderDialogController
            open={open}
            orderId={orderId}
            mode={mode}
            flowConfig={flowConfig}
            onBehalfOf={onBehalfOf}
            handleClose={handleClose}
            activeOrderTab={activeOrderTab}
            dialogType={dialogType}
            isOnBehalfCompleted={isOnBehalfCompleted}
            setIsOnBehalfCompleted={setIsOnBehalfCompleted}
            handleOutlookBookingCompleted={handleOutlookBookingCompleted}
        />
    )
}
